/**
 * Cart Add-on Handler
 * 
 * This script manages the synchronized removal of add-on products when a parent product is removed from cart
 */

class CartAddonHandler {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupEventListeners());
    } else {
      this.setupEventListeners();
    }
  }

  setupEventListeners() {
    // Listen for clicks on remove buttons in both cart page and cart drawer
    document.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-item-remove]');
      if (removeButton) {
        event.preventDefault();
        this.handleRemoveClick(removeButton);
        return;
      }

      // Handle minus button clicks
      const minusButton = event.target.closest('.cart__quantity-minus');
      if (minusButton) {
        const quantityInput = minusButton.closest('.cart__quantity').querySelector('.cart__quantity-field');
        if (quantityInput && parseInt(quantityInput.value) === 1) {
          // If quantity is 1 and minus is clicked, we're going to zero
          event.preventDefault();
          this.handleMinusToZero(quantityInput);
        }
      }
    });

    // Listen for quantity changes
    this.setupQuantityListeners();

    // Since the cart can be dynamically updated, watch for DOM changes
    this.observeCartChanges();
  }

  observeCartChanges() {
    // Create a MutationObserver to watch for changes to the cart
    const observer = new MutationObserver((mutations) => {
      // Check if we need to re-attach quantity listeners after DOM changes
      this.setupQuantityListeners();
    });

    // Watch for changes in the cart items container(s)
    const cartContainers = document.querySelectorAll('.cart__items, .drawer__items');
    if (cartContainers.length > 0) {
      cartContainers.forEach(container => {
        observer.observe(container, { childList: true, subtree: true });
      });
    }
  }

  setupQuantityListeners() {
    // Find all quantity input fields
    const quantityInputs = document.querySelectorAll('.cart__quantity-field');
    
    quantityInputs.forEach(input => {
      // Remove existing listeners to prevent duplicates
      input.removeEventListener('change', this.handleQuantityChange);
      
      // Add the event listener
      input.addEventListener('change', this.handleQuantityChange.bind(this));
    });
  }

  /**
   * Handle when the minus button is clicked on a quantity of 1
   */
  async handleMinusToZero(quantityInput) {
    const itemKey = quantityInput.dataset.id;
    if (!itemKey) return;
    
    // Get cart data to check if this is a parent product
    const response = await fetch('/cart.js');
    if (!response.ok) return;
    
    const cart = await response.json();
    
    // Find the item and check if it has a _unique_id property
    const item = cart.items.find(item => item.key === itemKey);
    if (!item || !item.properties) return;
    
    const uniqueId = item.properties._unique_id;
    
    // If this is a parent product, remove both parent and all add-ons
    if (uniqueId) {
      const addons = cart.items.filter(item => 
        item.properties && 
        item.properties._addon_for === uniqueId
      );
      
      if (addons.length > 0) {
        // Prepare updates for removing parent and all add-ons
        const updates = {};
        updates[itemKey] = 0; // Remove the parent
        
        // Remove all add-ons
        addons.forEach(addon => {
          updates[addon.key] = 0;
        });
        
        // Update the cart to remove everything at once
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ updates })
        });
        
        // Refresh the page or update the cart
        window.location.reload();
        return;
      }
    }
    
    // If it's not a parent product or has no add-ons, just submit a regular update
    const updates = {};
    updates[itemKey] = 0;
    
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ updates })
    });
    
    window.location.reload();
  }

  async handleQuantityChange(event) {
    const input = event.target;
    const newQuantity = parseInt(input.value);
    
    // Only process if the quantity is zero
    if (newQuantity !== 0) return;
    
    const itemKey = input.dataset.id;
    if (!itemKey) return;
    
    // Get cart data to check if this is a parent product
    const response = await fetch('/cart.js');
    if (!response.ok) return;
    
    const cart = await response.json();
    
    // Find the item and check if it has a _unique_id property
    const item = cart.items.find(item => item.key === itemKey);
    if (!item || !item.properties) return;
    
    const uniqueId = item.properties._unique_id;
    
    // If this is a parent product, find and remove all its add-ons
    if (uniqueId) {
      const addons = cart.items.filter(item => 
        item.properties && 
        item.properties._addon_for === uniqueId
      );
      
      if (addons.length > 0) {
        // Prepare updates for removing add-ons AND the parent
        const updates = {};
        updates[itemKey] = 0; // Remove the parent too
        
        // Remove all add-ons
        addons.forEach(addon => {
          updates[addon.key] = 0;
        });
        
        // Update the cart to remove the add-ons
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ updates })
        });
        
        // Refresh the page to show updated cart
        window.location.reload();
        return;
      }
    }
  }

  async handleRemoveClick(removeButton) {
    const itemKey = removeButton.dataset.id;
    if (!itemKey) return;

    // Get unique ID of the item being removed to check if it's a parent
    const parentItem = removeButton.closest('.cart__item');
    if (!parentItem) return;

    // Find the _unique_id property which identifies this item as a potential parent
    let uniqueId = null;

    // Check if this is a parent product with a unique ID by examining the cart data
    const response = await fetch('/cart.js');
    if (!response.ok) return;
    
    const cart = await response.json();
    
    // Find the item and check if it has a _unique_id property
    const item = cart.items.find(item => item.key === itemKey);
    if (!item || !item.properties) return;
    
    uniqueId = item.properties._unique_id;
    
    // If this is a parent product with a unique ID, we need to remove all its add-ons
    if (uniqueId) {
      // Find all add-ons with this parent unique ID
      const addons = cart.items.filter(item => 
        item.properties && 
        item.properties._addon_for === uniqueId
      );
      
      if (addons.length > 0) {
        // Prepare updates for removing parent and all add-ons
        const updates = {};
        updates[itemKey] = 0; // Remove the parent
        
        // Remove all add-ons
        addons.forEach(addon => {
          updates[addon.key] = 0;
        });
        
        // Update the cart to remove everything at once
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ updates })
        });
        
        // Refresh the page or update the cart
        window.location.reload();
        return;
      }
    }
    
    // If not a parent with add-ons, use the original link to remove the single item
    window.location.href = removeButton.href;
  }
}

// Initialize the handler
new CartAddonHandler(); 